import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import dayjs from 'dayjs';
import api from '../services/api';

export interface ShortUrl {
  hash: string;
  shortUrl: string;
  originalUrl: string;
  clicks: number;
  createdAt: string;
  expiresAt?: string;
  qrCode?: string;
  osintScore?: number;
  privacyScore?: number;
  isPasswordProtected?: boolean;
  burnAfterReading?: boolean;
  maxClicks?: number;
  metadata?: any;
}

export interface Analytics {
  summary: {
    totalClicks: number;
    last30Days: number;
    uniqueVisitors: number;
    trackersBlocked: number;
  };
  daily: Array<{
    date: string;
    clicks: number;
    unique: number;
    trackersBlocked: number;
  }>;
  hourly: Array<{
    hour: number;
    clicks: number;
  }>;
  countries: Array<{
    code: string;
    clicks: number;
  }>;
  devices: Array<{
    type: string;
    clicks: number;
  }>;
}

interface AppState {
  // URLs
  recentUrls: ShortUrl[];
  isCreating: boolean;
  isLoading: boolean;
  error: string | null;
  
  // User
  user: {
    email?: string;
    tier?: string;
  } | null;
  
  // Settings
  settings: {
    enableTrackerBlocking: boolean;
    defaultExpiration: '1d' | '7d' | '30d' | 'never';
    enableOfflineMode: boolean;
    theme: 'light' | 'dark' | 'system';
  };
  
  // Actions
  createShortUrl: (url: string, options?: {
    customAlias?: string;
    expiresIn?: '1d' | '7d' | '30d' | 'never';
    password?: string;
    maxClicks?: number;
    burnAfterReading?: boolean;
  }) => Promise<ShortUrl>;
  
  fetchRecentUrls: () => Promise<void>;
  deleteUrl: (hash: string) => Promise<void>;
  fetchAnalytics: (hash: string) => Promise<Analytics>;
  updateSettings: (settings: Partial<AppState['settings']>) => void;
  clearError: () => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      recentUrls: [],
      isCreating: false,
      isLoading: false,
      error: null,
      user: null,
      settings: {
        enableTrackerBlocking: true,
        defaultExpiration: '7d',
        enableOfflineMode: true,
        theme: 'system',
      },
      
      // Actions
      createShortUrl: async (url, options) => {
        set({ isCreating: true, error: null });
        
        try {
          const response = await api.createShortUrl({
            url,
            ...options,
            expiresIn: options?.expiresIn || get().settings.defaultExpiration,
          });
          
          const newUrl: ShortUrl = {
            ...response.data,
            originalUrl: url,
            clicks: response.data.clicks || 0,
            createdAt: dayjs().toISOString(),
          };
          
          set(state => ({
            recentUrls: [newUrl, ...state.recentUrls].slice(0, 50),
            isCreating: false,
          }));
          
          return newUrl;
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Failed to create short URL';
          set({ error: errorMessage, isCreating: false });
          throw new Error(errorMessage);
        }
      },
      
      fetchRecentUrls: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await api.getMyUrls();
          set({ 
            recentUrls: response.data.urls,
            isLoading: false,
          });
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Failed to fetch URLs';
          set({ error: errorMessage, isLoading: false });
        }
      },
      
      deleteUrl: async (hash) => {
        try {
          await api.deleteUrl(hash);
          set(state => ({
            recentUrls: state.recentUrls.filter(url => url.hash !== hash),
          }));
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Failed to delete URL';
          set({ error: errorMessage });
          throw new Error(errorMessage);
        }
      },
      
      fetchAnalytics: async (hash) => {
        const response = await api.getAnalytics(hash);
        return response.data;
      },
      
      updateSettings: (newSettings) => {
        set(state => ({
          settings: { ...state.settings, ...newSettings },
        }));
      },
      
      clearError: () => set({ error: null }),
    }),
    {
      name: 'anonlink-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        recentUrls: state.recentUrls,
        settings: state.settings,
      }),
    }
  )
);